import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { UploadCloud, X, CheckCircle2, AlertCircle, Loader2, Package, Factory, Sparkles, AlertTriangle } from "lucide-react";

const COLLECTIONS = [
  { slug: "the-doodles-official-collection", label: "The Doodles Official Collection" },
  { slug: "military-official-collection", label: "Military Official Collection" },
  { slug: "one-united-official-collection", label: "One United Official Collection" },
  { slug: "childrens-official-collection", label: "Children\u2019s Official Collection" },
  { slug: "seniors-collection", label: "Seniors Collection" },
  { slug: "the-encore-official-collection", label: "The Encore Official Collection" },
  { slug: "graphics-official-collection", label: "Graphics Official Collection" },
  { slug: "sports-official-collection", label: "Sports Official Collection" },
  { slug: "spine-official-collection", label: "Spine Official Collection" },
];

function niceName(filename) {
  return (filename || "")
    .replace(/\.[^.]+$/, "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, l => l.toUpperCase());
}

/**
 * Client-side downscale a File before upload.
 * Keeps prints crisp (3000px max) but drops size from 30-50MB PNGs to 2-4MB JPEG.
 * Auto-passthrough for tiny images (<3MB and <=3000px).
 * Preserves transparent PNGs (needed for print-on-demand).
 */
async function downscaleImage(file, { maxDim = 3000, quality = 0.92 } = {}) {
  // Skip resize for already-small files
  if (file.size < 3 * 1024 * 1024 && file.type !== "image/heic" && file.type !== "image/heif") {
    return file;
  }
  const bitmap = await createImageBitmap(file).catch(async () => {
    // Fallback via <img> for browsers without bitmap for HEIC
    return await new Promise((res, rej) => {
      const img = new Image();
      img.onload = () => res(img);
      img.onerror = rej;
      img.src = URL.createObjectURL(file);
    });
  });
  const w = bitmap.width || bitmap.naturalWidth;
  const h = bitmap.height || bitmap.naturalHeight;
  const scale = Math.min(1, maxDim / Math.max(w, h));
  const targetW = Math.round(w * scale);
  const targetH = Math.round(h * scale);
  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  // Keep alpha channel for PNG source (production needs transparency)
  const keepPng = file.type === "image/png";
  if (!keepPng) {
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, targetW, targetH);
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bitmap, 0, 0, targetW, targetH);
  const outType = keepPng ? "image/png" : "image/jpeg";
  const blob = await new Promise(res => canvas.toBlob(res, outType, quality));
  if (!blob) return file;
  const newName = file.name.replace(/\.(jpe?g|png|webp|heic|heif)$/i, keepPng ? ".png" : ".jpg");
  return new File([blob], newName, { type: outType, lastModified: Date.now() });
}

export default function BulkUpload() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const fileInput = useRef(null);

  // Step 1: Upload queue
  const [rows, setRows] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0, errors: [] });

  // Step 2: Apply
  const [designs, setDesigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedDesigns, setSelectedDesigns] = useState(new Set());
  const [selectedTemplates, setSelectedTemplates] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState("draft");
  const [applyJob, setApplyJob] = useState(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) nav("/auth");
  }, [user, loading, nav]);

  const refreshDesigns = useCallback(async () => {
    try {
      const url = statusFilter === "all" ? "/admin/designs" : `/admin/designs?status=${statusFilter}`;
      const r = await api.get(url);
      setDesigns(r.data.designs);
    } catch {}
  }, [statusFilter]);

  const refreshTemplates = useCallback(async () => {
    try {
      const r = await api.get("/admin/product-templates");
      setTemplates(r.data.templates);
    } catch {}
  }, []);

  useEffect(() => { if (user?.is_admin) { refreshDesigns(); refreshTemplates(); } }, [user, refreshDesigns, refreshTemplates]);

  // --- Step 1 handlers ---
  const addFiles = async (files) => {
    const arr = Array.from(files).filter(f => f.type.startsWith("image/"));
    if (arr.length === 0) return;
    // Big files (>3MB) get downscaled in-browser so uploads succeed reliably
    const bigOnes = arr.filter(f => f.size >= 3 * 1024 * 1024);
    if (bigOnes.length > 0) {
      toast.info(`Optimizing ${bigOnes.length} large image${bigOnes.length > 1 ? "s" : ""} for faster upload…`);
    }
    const processed = [];
    for (const f of arr) {
      try {
        const resized = await downscaleImage(f);
        const savedPct = f.size > 0 ? Math.round((1 - resized.size / f.size) * 100) : 0;
        if (resized !== f && savedPct >= 20) {
          console.log(`Optimized ${f.name}: ${(f.size/1e6).toFixed(1)}MB → ${(resized.size/1e6).toFixed(1)}MB (-${savedPct}%)`);
        }
        processed.push(resized);
      } catch (err) {
        console.warn(`Resize failed for ${f.name}, uploading original:`, err);
        processed.push(f);
      }
    }
    setRows(prev => [
      ...prev,
      ...processed.map(f => ({
        file: f,
        name: niceName(f.name),
        description: "",
        collections: ["graphics-official-collection"],
        slug: "",
        tags: [],
        ai: null,
        aiStatus: null,
      })),
    ]);
  };
  const removeRow = (i) => setRows(prev => prev.filter((_, idx) => idx !== i));
  const updateRow = (i, patch) => setRows(prev => prev.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const toggleRowColl = (i, c) => setRows(prev => prev.map((r, idx) => idx === i ? {
    ...r,
    collections: r.collections.includes(c) ? r.collections.filter(x=>x!==c) : [...r.collections, c],
  } : r));

  const analyzeRow = async (i) => {
    const r = rows[i];
    if (!r || r.aiStatus === "analyzing") return;
    updateRow(i, { aiStatus: "analyzing" });
    try {
      const fd = new FormData();
      fd.append("file", r.file);
      fd.append("selected_collections", r.collections.join(","));
      const res = await api.post("/admin/ai/analyze-design", fd, { headers: {"Content-Type":"multipart/form-data"}, timeout: 60000 });
      const ai = res.data;
      updateRow(i, {
        ai,
        aiStatus: "done",
        name: ai.name || r.name,
        description: ai.description || r.description,
        collections: [...new Set([...(ai.suggested_collections || []), ...r.collections])],
        slug: ai.slug || r.slug,
        tags: ai.tags || r.tags,
      });
      if (ai.is_duplicate) toast.warning(`Possible duplicate of "${ai.duplicate_of}"`);
    } catch (e) {
      updateRow(i, { aiStatus: "error" });
      const msg = e.response?.data?.detail || e.message || "AI analysis failed";
      toast.error(`AI failed on "${r.name}": ${msg.slice(0, 100)}`);
    }
  };

  const analyzeAll = async () => {
    const pending = rows.map((r, i) => ({ r, i })).filter(x => x.r.aiStatus !== "done");
    if (pending.length === 0) { toast.info("All designs already analyzed"); return; }
    toast.info(`Analyzing ${pending.length} designs — this takes ~3 sec each`);
    // Process in batches of 3 concurrent to avoid rate limits and stay responsive
    const BATCH = 3;
    let ok = 0, fail = 0;
    for (let start = 0; start < pending.length; start += BATCH) {
      const chunk = pending.slice(start, start + BATCH);
      await Promise.all(chunk.map(async ({ i }) => {
        try { await analyzeRow(i); ok++; } catch { fail++; }
      }));
    }
    if (fail === 0) toast.success(`AI analyzed all ${ok} designs`);
    else toast.warning(`Analyzed ${ok} · ${fail} failed — check individual rows`);
  };

  const uploadDrafts = async () => {
    if (rows.length === 0) { toast.error("Add designs first"); return; }
    setUploading(true);
    setUploadProgress({ done: 0, total: rows.length, errors: [] });
    const errors = [];
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      try {
        const fd = new FormData();
        fd.append("file", r.file);
        fd.append("name", r.name);
        fd.append("description", r.description);
        fd.append("collections", r.collections.join(","));
        if (r.slug) fd.append("slug", r.slug);
        if (r.tags && r.tags.length) fd.append("tags", r.tags.join(","));
        await api.post("/admin/designs/draft", fd, { headers: {"Content-Type":"multipart/form-data"} });
        setUploadProgress(p => ({ ...p, done: p.done + 1 }));
      } catch (e) {
        errors.push(`${r.name}: upload failed`);
        setUploadProgress(p => ({ ...p, done: p.done + 1, errors: [...p.errors, r.name] }));
      }
    }
    setUploading(false);
    if (errors.length === 0) {
      toast.success(`Uploaded ${rows.length} designs as drafts — scroll down to apply to templates`);
    } else {
      toast.warning(`Uploaded ${rows.length - errors.length}/${rows.length} — ${errors.length} failed`);
    }
    setRows([]);
    await refreshDesigns();
  };

  // --- Step 2 handlers ---
  const toggleDesign = (id) => setSelectedDesigns(s => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleTemplate = (id) => setSelectedTemplates(s => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const selectAllTemplates = (source) => setSelectedTemplates(s => {
    const n = new Set(s);
    templates.filter(t => t.fulfillment_source === source).forEach(t => n.add(t.template_id));
    return n;
  });
  const clearTemplates = () => setSelectedTemplates(new Set());
  const selectAllDesigns = () => setSelectedDesigns(new Set(designs.map(d => d.design_id)));
  const clearDesigns = () => setSelectedDesigns(new Set());

  const applyToTemplates = async () => {
    if (selectedDesigns.size === 0) { toast.error("Select at least one design"); return; }
    if (selectedTemplates.size === 0) { toast.error("Select at least one template"); return; }
    setApplying(true);
    try {
      const r = await api.post("/admin/designs/apply", {
        design_ids: [...selectedDesigns],
        template_ids: [...selectedTemplates],
        set_status_active: true,
      });
      setApplyJob({ job_id: r.data.job_id, total: r.data.total, done: 0, failed: 0, status: "running" });
      toast.success(`Publishing ${r.data.total} products in background`);
      setSelectedDesigns(new Set());
      setSelectedTemplates(new Set());
    } catch (e) {
      toast.error("Apply failed");
    } finally { setApplying(false); }
  };

  useEffect(() => {
    if (!applyJob?.job_id || applyJob.status === "complete") return;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/admin/designs/apply/${applyJob.job_id}`);
        setApplyJob(r.data);
        if (r.data.status === "complete") {
          clearInterval(t);
          refreshDesigns();
          toast.success(`Published ${r.data.done} products${r.data.failed ? ` (${r.data.failed} failed)` : ""}`);
        }
      } catch {}
    }, 2500);
    return () => clearInterval(t);
  }, [applyJob?.job_id, applyJob?.status, refreshDesigns]);

  const archive = async (id) => {
    try { await api.patch(`/admin/designs/${id}`, { status: "archived" }); toast.success("Archived"); refreshDesigns(); }
    catch { toast.error("Failed"); }
  };
  const activate = async (id) => {
    try { await api.patch(`/admin/designs/${id}`, { status: "active" }); toast.success("Activated"); refreshDesigns(); }
    catch { toast.error("Failed"); }
  };

  if (!user?.is_admin) return null;

  const selfTemplates = templates.filter(t => t.fulfillment_source === "SELF");
  const printifyTemplates = templates.filter(t => t.fulfillment_source === "PRINTIFY");

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="bulk-upload-page">
      <h1 className="font-display text-5xl text-[color:var(--hb-navy)]">Bulk Design Studio</h1>
      <p className="text-sm text-neutral-600 mt-2">
        Upload designs as <b>drafts</b>, then apply them to product templates.
        T-Shirts print <b>in-house (SELF)</b>. Everything else via <b>Printify</b>.
      </p>
      <p className="text-xs text-[color:var(--hb-royal)] mt-1 flex items-center gap-1">
        <Sparkles size={12}/> AI can auto-name each design, write descriptions, and suggest collections — hit the &ldquo;AI&rdquo; button on any row.
      </p>

      {/* ============ STEP 1 ============ */}
      <div className="mt-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-full bg-[color:var(--hb-navy)] text-white flex items-center justify-center text-sm font-medium">1</div>
          <h2 className="font-display text-3xl">Upload Designs</h2>
        </div>

        <div
          onDragOver={e=>{e.preventDefault();setDragOver(true);}}
          onDragLeave={()=>setDragOver(false)}
          onDrop={e=>{e.preventDefault();setDragOver(false);addFiles(e.dataTransfer.files);}}
          onClick={()=>fileInput.current?.click()}
          className={`border-2 border-dashed ${dragOver?"border-[color:var(--hb-royal)] bg-blue-50":"border-neutral-300"} p-12 text-center cursor-pointer bg-white`}
          data-testid="bulk-drop-zone"
        >
          <UploadCloud className="mx-auto text-neutral-400 mb-3" size={40}/>
          <p className="text-sm font-medium">Drop PNG designs here or click to browse</p>
          <p className="text-xs text-neutral-500 mt-1">Transparent background, ~2000×2000px works best</p>
          <input ref={fileInput} type="file" multiple accept="image/png,image/jpeg" onChange={e=>addFiles(e.target.files)} className="hidden" data-testid="bulk-file-input"/>
        </div>

        {rows.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium">{rows.length} design{rows.length>1?"s":""} ready to upload as drafts</div>
              <div className="flex gap-2">
                <button onClick={analyzeAll} className="text-xs uppercase tracking-widest border border-[color:var(--hb-royal)] text-[color:var(--hb-royal)] px-3 py-2 flex items-center gap-1 hover:bg-[color:var(--hb-royal)] hover:text-white" data-testid="ai-analyze-all">
                  <Sparkles size={12}/> AI Name All
                </button>
                <button onClick={()=>setRows([])} className="text-xs uppercase tracking-widest border border-neutral-300 px-3 py-2">Clear</button>
                <button onClick={uploadDrafts} disabled={uploading} className="hb-btn-primary" data-testid="bulk-upload-btn">
                  {uploading ? `Uploading ${uploadProgress.done}/${uploadProgress.total}…` : `Upload ${rows.length} as Drafts`}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {rows.map((r, i) => (
                <div key={i} className="border border-neutral-200 bg-white p-3 grid grid-cols-[80px_1fr_auto] gap-3 items-start" data-testid={`bulk-row-${i}`}>
                  <div className="w-20 h-20 bg-[color:var(--hb-warm)] overflow-hidden relative">
                    <img src={URL.createObjectURL(r.file)} alt="" className="w-full h-full object-contain" />
                    {r.ai?.is_duplicate && (
                      <div className="absolute inset-0 bg-amber-500/80 flex items-center justify-center text-white text-[9px] text-center px-1 font-bold" title={`Duplicate of ${r.ai.duplicate_of}`}>
                        <AlertTriangle size={12}/>
                      </div>
                    )}
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <input value={r.name} onChange={e=>updateRow(i,{name:e.target.value})} placeholder="Design name" className="flex-1 border-b border-neutral-300 py-1 text-sm font-medium" data-testid={`bulk-name-${i}`}/>
                      <button
                        type="button"
                        onClick={()=>analyzeRow(i)}
                        disabled={r.aiStatus === "analyzing"}
                        className={`text-[10px] uppercase tracking-widest px-2 py-1 border flex items-center gap-1 shrink-0 ${
                          r.aiStatus === "done" ? "border-green-500 text-green-700 bg-green-50" :
                          r.aiStatus === "error" ? "border-red-400 text-red-600" :
                          r.aiStatus === "analyzing" ? "border-neutral-300 text-neutral-400" :
                          "border-[color:var(--hb-royal)] text-[color:var(--hb-royal)] hover:bg-[color:var(--hb-royal)] hover:text-white"
                        }`}
                        data-testid={`ai-analyze-${i}`}
                        title="Use AI to suggest name, description, and collections"
                      >
                        {r.aiStatus === "analyzing" ? <Loader2 size={10} className="animate-spin"/> :
                         r.aiStatus === "done" ? <CheckCircle2 size={10}/> :
                         <Sparkles size={10}/>}
                        AI
                      </button>
                    </div>
                    <input value={r.description} onChange={e=>updateRow(i,{description:e.target.value})} placeholder="Description (optional)" className="w-full border-b border-neutral-200 py-1 text-xs" data-testid={`bulk-desc-${i}`}/>
                    {r.ai?.is_duplicate && (
                      <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 flex items-center gap-1">
                        <AlertTriangle size={10}/> Possible duplicate of &ldquo;{r.ai.duplicate_of}&rdquo;
                      </div>
                    )}
                    {r.ai?.grade && (
                      <div className="flex items-start gap-2 text-[10px] pt-1" data-testid={`grade-row-${i}`}>
                        <div className={`shrink-0 px-2 py-1 font-bold text-xs uppercase tracking-wider ${
                          r.ai.grade.grade === "A" ? "bg-green-100 text-green-800 border border-green-300" :
                          r.ai.grade.grade === "B" ? "bg-[color:var(--hb-royal)]/10 text-[color:var(--hb-royal)] border border-[color:var(--hb-royal)]/30" :
                          r.ai.grade.grade === "C" ? "bg-amber-100 text-amber-800 border border-amber-300" :
                          r.ai.grade.grade === "D" ? "bg-orange-100 text-orange-800 border border-orange-300" :
                          "bg-red-100 text-red-800 border border-red-300"
                        }`} title={`Print-readiness score: ${r.ai.grade.score}/100`}>
                          {r.ai.grade.grade} · {r.ai.grade.score}
                        </div>
                        <div className="flex-1 text-neutral-600 leading-relaxed">
                          <span>{r.ai.grade.resolution?.message}</span>
                          {r.ai.grade.issues?.length > 0 && (
                            <div className="mt-0.5 text-red-700">
                              {r.ai.grade.issues.map((iss, idx) => <div key={idx}>⚠ {iss}</div>)}
                            </div>
                          )}
                          {r.ai.grade.tips?.length > 0 && (
                            <div className="mt-0.5 text-neutral-500">
                              {r.ai.grade.tips.map((tip, idx) => <div key={idx}>→ {tip}</div>)}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {r.tags && r.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 pt-1">
                        {r.tags.slice(0,6).map((t, ti) => (
                          <span key={ti} className="text-[9px] uppercase tracking-widest bg-[color:var(--hb-royal)]/10 text-[color:var(--hb-royal)] px-1.5 py-0.5 border border-[color:var(--hb-royal)]/20">
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-1 pt-1">
                      {COLLECTIONS.map(c => (
                        <button key={c.slug} onClick={()=>toggleRowColl(i,c.slug)} type="button"
                          className={`px-1.5 py-0.5 text-[10px] uppercase tracking-widest border ${r.collections.includes(c.slug)?"bg-[color:var(--hb-navy)] text-white border-[color:var(--hb-navy)]":"border-neutral-300"}`}>
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <button onClick={()=>removeRow(i)} className="text-neutral-400 hover:text-[color:var(--hb-red)]" data-testid={`bulk-remove-${i}`}><X size={16}/></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ============ STEP 2 ============ */}
      <div className="mt-16 border-t border-neutral-200 pt-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-full bg-[color:var(--hb-navy)] text-white flex items-center justify-center text-sm font-medium">2</div>
          <h2 className="font-display text-3xl">Apply Designs to Templates</h2>
        </div>
        <p className="text-xs text-neutral-500 mb-6">Select any number of designs, then choose which product templates to publish them as. Products inherit the template's fulfillment source (SELF or PRINTIFY).</p>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Designs picker */}
          <div className="border border-neutral-200 bg-white p-4" data-testid="designs-picker">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-sm uppercase tracking-widest">Designs ({selectedDesigns.size} selected)</h3>
              <div className="flex gap-1">
                {["draft","active","archived","all"].map(s => (
                  <button key={s} onClick={()=>setStatusFilter(s)}
                    className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${statusFilter===s?"bg-[color:var(--hb-navy)] text-white border-[color:var(--hb-navy)]":"border-neutral-300"}`}
                    data-testid={`filter-${s}`}>{s}</button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 mb-3 text-[10px] uppercase tracking-widest">
              <button onClick={selectAllDesigns} className="border border-neutral-300 px-2 py-1">Select all</button>
              <button onClick={clearDesigns} className="border border-neutral-300 px-2 py-1">Clear</button>
            </div>
            <div className="max-h-96 overflow-y-auto divide-y divide-neutral-100" data-testid="designs-list-picker">
              {designs.length === 0 && <div className="text-center text-xs text-neutral-500 py-8">No designs with status "{statusFilter}"</div>}
              {designs.map(d => (
                <label key={d.design_id} className="flex items-center gap-3 py-2 cursor-pointer hover:bg-neutral-50 px-1" data-testid={`design-item-${d.design_id}`}>
                  <input type="checkbox" checked={selectedDesigns.has(d.design_id)} onChange={()=>toggleDesign(d.design_id)} className="shrink-0"/>
                  <div className="w-12 h-12 bg-[color:var(--hb-warm)] shrink-0 overflow-hidden">
                    {d.design_path && <img src={fileUrl(d.design_path)} alt="" className="w-full h-full object-contain"/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{d.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                      <span className={`inline-block px-1.5 py-0.5 mr-1 ${d.status==="draft"?"bg-amber-100 text-amber-800":d.status==="archived"?"bg-neutral-200":"bg-green-100 text-green-800"}`}>{d.status}</span>
                      {d.product_count} products
                    </div>
                  </div>
                  {d.status === "active" ? (
                    <button type="button" onClick={(e)=>{e.preventDefault();archive(d.design_id);}} className="text-[10px] uppercase tracking-widest border border-neutral-300 px-2 py-1">Archive</button>
                  ) : d.status === "archived" ? (
                    <button type="button" onClick={(e)=>{e.preventDefault();activate(d.design_id);}} className="text-[10px] uppercase tracking-widest border border-neutral-300 px-2 py-1">Restore</button>
                  ) : null}
                </label>
              ))}
            </div>
          </div>

          {/* Templates picker */}
          <div className="border border-neutral-200 bg-white p-4" data-testid="templates-picker">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-sm uppercase tracking-widest">Templates ({selectedTemplates.size} selected)</h3>
              <button onClick={clearTemplates} className="text-[10px] uppercase tracking-widest border border-neutral-300 px-2 py-1">Clear</button>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-xs font-medium text-[color:var(--hb-navy)]">
                  <Factory size={14}/> SELF · In-house print
                </div>
                <button onClick={()=>selectAllTemplates("SELF")} className="text-[10px] uppercase tracking-widest border border-neutral-300 px-2 py-1">Select all SELF</button>
              </div>
              <div className="space-y-1">
                {selfTemplates.map(t => (
                  <label key={t.template_id} className="flex items-center gap-2 py-1.5 px-2 border border-neutral-100 hover:bg-neutral-50 cursor-pointer" data-testid={`tpl-${t.template_id}`}>
                    <input type="checkbox" checked={selectedTemplates.has(t.template_id)} onChange={()=>toggleTemplate(t.template_id)}/>
                    <span className="flex-1 text-sm">{t.name}</span>
                    <span className="text-xs text-neutral-500">${t.base_price.toFixed(2)}</span>
                    <span className="text-[9px] uppercase tracking-widest bg-[color:var(--hb-navy)] text-white px-1.5 py-0.5">SELF</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-xs font-medium text-[color:var(--hb-royal)]">
                  <Package size={14}/> PRINTIFY · Outsourced
                </div>
                <button onClick={()=>selectAllTemplates("PRINTIFY")} className="text-[10px] uppercase tracking-widest border border-neutral-300 px-2 py-1">Select all PRINTIFY</button>
              </div>
              <div className="space-y-1">
                {printifyTemplates.map(t => (
                  <label key={t.template_id} className="flex items-center gap-2 py-1.5 px-2 border border-neutral-100 hover:bg-neutral-50 cursor-pointer" data-testid={`tpl-${t.template_id}`}>
                    <input type="checkbox" checked={selectedTemplates.has(t.template_id)} onChange={()=>toggleTemplate(t.template_id)}/>
                    <span className="flex-1 text-sm">{t.name}</span>
                    <span className="text-xs text-neutral-500">${t.base_price.toFixed(2)}</span>
                    <span className="text-[9px] uppercase tracking-widest bg-[color:var(--hb-royal)] text-white px-1.5 py-0.5">PRINTIFY</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between border-t border-neutral-200 pt-6">
          <div className="text-xs text-neutral-600">
            Will publish <b>{selectedDesigns.size} × {selectedTemplates.size} = {selectedDesigns.size * selectedTemplates.size}</b> products
          </div>
          <button onClick={applyToTemplates} disabled={applying || selectedDesigns.size===0 || selectedTemplates.size===0}
                  className="hb-btn-primary" data-testid="apply-btn">
            {applying ? "Starting…" : "Publish Selected"}
          </button>
        </div>

        {applyJob && (
          <div className="mt-6 border border-neutral-200 bg-white p-4" data-testid="apply-status">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium">Publishing job {applyJob.job_id}</div>
              <div className="text-xs text-neutral-500">
                {applyJob.status === "complete" ? (
                  <span className="flex items-center gap-1 text-green-700"><CheckCircle2 size={14}/>Complete</span>
                ) : (
                  <span className="flex items-center gap-1"><Loader2 size={14} className="animate-spin"/>Running</span>
                )}
              </div>
            </div>
            <div className="w-full h-2 bg-neutral-200">
              <div className="h-2 bg-[color:var(--hb-royal)] transition-all" style={{width: `${applyJob.total ? ((applyJob.done+applyJob.failed)/applyJob.total)*100 : 0}%`}}/>
            </div>
            <div className="text-xs text-neutral-500 mt-2">{applyJob.done} done · {applyJob.failed} failed · {applyJob.total} total</div>
          </div>
        )}
      </div>
    </div>
  );
}
